import { createColumnHelper, type AccessorColumnDef, type RowData } from "@tanstack/react-table";
import { metrics } from "../../views/merchandise/metrics";
import { COMPARE_METRIC_LOOKUP, METRIC_GROUPING_LOOKUP } from "../../views/merchandise/tables/metricsByEntity";
import { MetricCell } from "./cells/MetricCell";

interface OverrideOptions {
  formatOptions?: {
    decimalPlaces?: number;
    compact?: boolean;
    clipDecimalPlaces?: boolean;
    format?: "number" | "percent" | "currency";
    overrideMarketplace?: string;
  };
}

const buildMetricColumn = <TData extends RowData>(
  id: string,
  options: OverrideOptions = {},
): AccessorColumnDef<TData, number | null> => {
  const currentValueDef = metrics.find((m) => m.id === id);
  if (!currentValueDef) {
    throw new Error(`Metric with id ${id} not found`);
  }
  if (!["number", "percent", "currency"].includes(currentValueDef.format)) {
    throw new Error(`Unsupported format ${currentValueDef.format}`);
  }

  const compareToDef = metrics.find((m) => m.id === COMPARE_METRIC_LOOKUP[id]);
  if (!compareToDef) {
    // throw new Error(`Compare metric for id ${id} not found`);

    // NOTE: having no compare breaks the formatting for currency since not all options are applied. Is this intentional or
    // should we return the MetricCell just with a blank compare value to still get proper formatting?

    // Should throw here, since we have non-metrics in the metric registry, but try to make a data column until it's fixed. Output to console to nag devs.
    // eslint-disable-next-line no-console
    console.log(`Compare metric for id ${id} not found`);

    return {
      id,
      header: currentValueDef.name,
      accessorFn: currentValueDef.accessor,
      cell: ({ getValue }) => (
        <MetricCell
          value={getValue()}
          formatOptions={{
            format: currentValueDef.format as "number" | "percent" | "currency",
            decimalPlaces: currentValueDef.decimalPlaces,
            clipDecimalPlaces: currentValueDef.clipDecimalPlaces,
            ...(options.formatOptions ?? {}),
          }}
          compareHighlight={currentValueDef.compareHighlight}
        />
      ),
      minSize: 75,
      maxSize: 200,
      size: 120,
      meta: {
        columnFilterOptions: {
          format: currentValueDef.format,
        },
        columnSelectorOptions: {
          description: currentValueDef.toolTip,
          grouping: METRIC_GROUPING_LOOKUP[id],
        },
        justify: "end",
      },
    };
  }
  if (!["number", "percent", "currency"].includes(compareToDef.format)) {
    throw new Error(`Unsupported compare metric format ${compareToDef.format}`);
  }

  return {
    id,
    header: currentValueDef.name,
    accessorFn: currentValueDef.accessor,
    cell: ({ getValue, row }) => (
      <MetricCell
        value={getValue()}
        compareValue={compareToDef.accessor(row.original)}
        formatOptions={{
          format: currentValueDef.format as "number" | "percent" | "currency",
          decimalPlaces: currentValueDef.decimalPlaces,
          clipDecimalPlaces: currentValueDef.clipDecimalPlaces,
          compact: false,
          ...(options.formatOptions ?? {}),
        }}
        compareFormatOptions={{
          format: compareToDef.format as "number" | "percent" | "currency",
          decimalPlaces: compareToDef.decimalPlaces,
          addPlus: true,
        }}
        compareHighlight={currentValueDef.compareHighlight}
      />
    ),
    minSize: 75,
    maxSize: 200,
    size: 120,
    meta: {
      columnFilterOptions: {
        format: currentValueDef.format,
      },
      columnSelectorOptions: {
        description: currentValueDef.toolTip,
        grouping: METRIC_GROUPING_LOOKUP[id],
      },
      justify: "end",
    },
  };
};

/**
 * Extend @tanstack/table's column helper to build columns from definitions insteand of only custom columns
 */
export function createColumnBuilder<TData extends RowData>() {
  const columnHelper = createColumnHelper<TData>();
  return {
    ...columnHelper,
    metric: buildMetricColumn<TData>,
  };
}
